import React, { useCallback, useState } from 'react';
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';

import { AthleteFormValues } from 'features/addPlayer/interface';
import Select from 'shared/form/select/select';
import { DateRange } from 'shared/form/datepicker/dateRange';
import moment from 'moment';
import get from 'lodash/get';
import { InfoIcon, PlusIcon } from 'shared/icon';
import AddNoteModal from 'shared/components/modal/addNoteModal';
import useModal from 'shared/hooks/useModal';
import { NoteModal } from 'features/playerProfile/constant';
import ContractTypeInfo from 'shared/components/modal/contractTypeInfo';

interface IProps {
	register: UseFormRegister<AthleteFormValues>;
	control: Control<AthleteFormValues, any>;
	errors: FieldErrors<any>;
	setValue: UseFormSetValue<AthleteFormValues>;
	isFormSubmit: boolean;
	getValues: any;
}

const ContractInfo: React.FC<IProps> = (props) => {
	const { control, setValue, isFormSubmit, errors, getValues } = props;

	const { isOpen, type, closeModal, openModal } = useModal<NoteModal>();

	const [note, setNote] = useState('');

	const getErrorMsg = useCallback(
		(key: string) => {
			return get(errors, `${key}.message`, '') as string;
		},
		[errors]
	);

	const watchedStartDate = useWatch({
		control,
		name: 'contract_information.retainer_term_start'
	});
	const watchedEndDate = useWatch({
		control,
		name: 'contract_information.retainer_term_end'
	});

	const watchedType = useWatch({
		control,
		name: 'contract_information.type'
	});

	const contractTypeError = !watchedType && watchedStartDate && watchedEndDate && 'Contract type is required';

	return (
		<div className='mb-4'>
			<div className='flex justify-between items-center'>
				<p className='title-text'>Contract Information</p>
				<div
					className='flex items-center cursor-pointer title-text '
					onClick={() => {
						openModal(NoteModal.addNote);
					}}
				>
					<PlusIcon className='stroke-primary size-3 mr-2' />
					<p className='text-sm font-semibold text-primary'>Add Note</p>
				</div>
			</div>
			<div className='grid grid-cols-3 gap-x-4 gap-y-2'>
				<div className='relative '>
					<Select
						placeHolder='Select Individual Contract'
						control={control}
						name='contract_information.type'
						title='Individual Contract'
						options={INDIVIDUAL_TYPE_OPTIONS}
						required={!!watchedStartDate && !!watchedEndDate}
					/>
					<div className='absolute right-1 top-[0px]' onClick={() => openModal(NoteModal.contractType)}>
						<InfoIcon className={`stroke-[#667085] cursor-pointer `} height='13px' width='13px' />
					</div>

					{contractTypeError && <p className='text-red-500 text-xs mt-1'>{contractTypeError}</p>}
				</div>

				<DateRange
					onCancel={() => {
						setValue('contract_information.retainer_term_start', '');
						setValue('contract_information.retainer_term_end', '');
					}}
					title='Contract Period'
					onChange={(Range) => {
						setValue(
							'contract_information.retainer_term_start',
							moment(Range.startDate).utc().toISOString()
						);
						setValue('contract_information.retainer_term_end', moment(Range.endDate).utc().toISOString());
					}}
					isFormSubmit={isFormSubmit}
					dateRange={{
						startDate: getValues('contract_information.retainer_term_start') || '',
						endDate: getValues('contract_information.retainer_term_end') || ''
					}}
					errorMsg={getErrorMsg('contract_information.retainer_term_start')}
					className='right-0'
				/>
			</div>
			{isOpen && type === NoteModal.addNote && (
				<AddNoteModal
					headerTile={'Add Note for Contract'}
					show={isOpen}
					onClose={() => {
						closeModal();
						setNote({} as any);
					}}
					subHeaderTitle={' '}
					isQuickNote={true}
					formSubmit={(data) => {
						setNote(data);
						setValue('contract_information.contract_note', data.note);
					}}
					initialData={note}
					type={type}
				/>
			)}
			{isOpen && type === NoteModal.contractType && <ContractTypeInfo show={isOpen} onClose={closeModal} />}
		</div>
	);
};

export default ContractInfo;

export const CONTRACT_TYPE_OPTIONS = [
	{ label: 'No Contract', value: 'No Contract' },
	{ label: 'Head to Toe', value: 'Head to Toe' },
	{ label: 'Racquet', value: 'Racquet' },
	{ label: 'Expired', value: 'Expired' }
];

export const TENNIS_CONTRACT_TYPE_OPTIONS = [
	{ label: 'No Contract', value: 'No Contract' },
	{ label: 'Equipments', value: 'Equipments' },
	{ label: 'Head to Toe', value: 'Head to Toe' },
	{ label: 'Monetary', value: 'Monetary' },
	{ label: 'National', value: 'National' }
];

export const INDIVIDUAL_TYPE_OPTIONS = [
	{ label: 'Type A', value: 'Type A' },
	{ label: 'Type B', value: 'Type B' },
	{ label: 'Type C', value: 'Type C' },
	{ label: 'Type D', value: 'Type D' },
	{ label: 'Type E', value: 'Type E' },
	{ label: 'Type F', value: 'Type F' }
];
